import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import FilterPanelCheckboxGroup from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import FilterSearchInput from 'components/Common/FilterPanel/FilterSearchInput'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import { getTourGroupTypesList } from 'components/Search/TourV2/TourV2SearchGroupSelect'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import type { OfferListSortOption } from 'constants/offerListFilters'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'
import config from 'constants/config'
import {
  isTourSearchSpecialOfferCampaignActive,
  TOUR_OFFER_TAG,
  TOUR_OFFER_TAG_LIST,
  TOURS_GROUP_TYPE,
  TOURS_GROUP_TYPES_LIST,
  TOUR_SEARCH_SPECIAL_OFFERS_CAMPAIGNS,
  type TourSearchSpecialOfferCampaignIntlKey,
} from 'constants/tours'
import { EmptyArray, max } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'

import TextButton from 'LuxKit/Button/TextButton'
import RadioInput from 'LuxKit/Radio/RadioInput'
import DualSlider from 'LuxKit/Slider/DualSlider'
import DualSliderInput from 'LuxKit/Slider/DualSliderInput'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LuxPlusLabel from 'LuxKit/Label/LuxPlusLabel'
import TourSearchSocieteFilters from 'components/Tours/TourSearchSocieteFilters'
import { rem } from 'polished'

import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useState,
} from 'react'

import * as React from 'react'
import { defineMessages, useIntl } from 'react-intl'
import type { IntlShape, MessageDescriptor } from 'react-intl'
import productLabels from 'i18n/productLabels'
import styled from 'styled-components'
import { getIsLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'
import GeoContext from 'contexts/geoContext'
import { useAppSelector } from '../../hooks/reduxHooks'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'
import { triplePointCampaignId, triplePointsTakeoverId } from 'takeovers/societyTriplePointsTakeover/societyTriplePointsTakeoverConfig'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import useLoyaltyFilterLabels from 'hooks/Search/useLoyaltyFilterLabels'

const InputGroup = styled(Group)`
  margin-top: ${rem(16)};
`

const ColourfulLoadingAnimation = styled(LoadingAnimation)`
  margin-top: ${rem(16)};
  color: ${(props) => props.theme.palette.brand.primary.normal};
`

const ActionTextButton = styled(TextButton)`
  padding: 0;
`

const specialOfferMessages: Record<TourSearchSpecialOfferCampaignIntlKey, MessageDescriptor> = defineMessages({
  toursCampaign: {
    id: 'JbhT-9E-_RBh6DX9NMKR',
    defaultMessage: 'Asia Tour Sale',
    description: 'Label for the reusable tours campaign filter option in the special offers section',
  },
})

function toggleSelectedSet(selectedSet: Set<string>, value: string): Set<string> {
  const nextSelectedSet = new Set([...selectedSet.values()])
  if (nextSelectedSet.has(value)) {
    nextSelectedSet.delete(value)
  } else {
    nextSelectedSet.add(value)
  }
  return nextSelectedSet
}

interface Option {
  value: string
  label?: string
  count: number
}

export function generateOptionItems(
  options: Array<Option>,
  localStates: Set<string>,
  filterString = '',
  intl: IntlShape,
) {
  return options.filter((option) =>
    option.label?.toLowerCase().includes(filterString.toLowerCase()) || localStates.has(option.value),
  ).sort((a, b) => {
    if (localStates.has(a.value) && !localStates.has(b.value)) return -1
    if (!localStates.has(a.value) && localStates.has(b.value)) return 1
    if (a.count > b.count) return -1
    if (a.count < b.count) return 1
    return 0
  }).map(({ value, label, count }) => ({
    label: (
      <Group direction="horizontal" horizontalAlign="space-between">
        {label ?? value}
        <span>
          {intl.formatMessage(
            {
              id: 'M1ulPasl_9AbZ0NSZs_F',
              defaultMessage: '({count})',
              description: 'Parenthesised count of matching results shown next to each filter option in tour search filters',
            },
            { count },
          )}
        </span>
      </Group>
    ),
    value,
    checked: localStates.has(value),
  }))
}

const GROUP_TYPE_SLUG_TO_ENUM: Record<string, string> = {
  family_friendly: TOURS_GROUP_TYPE.isFamilyFriendly,
  small_group: TOURS_GROUP_TYPE.isSmallGroup,
  large_group: TOURS_GROUP_TYPE.isLargeGroup,
  private_request: TOURS_GROUP_TYPE.isPrivateRequest,
}

const OFFER_TAG_SLUG_TO_ENUM: Record<string, string> = {
  on_sale: TOUR_OFFER_TAG.onSale,
  flash_offer: TOUR_OFFER_TAG.flashOffer,
}

function groupTypeFacetsToOptions(
  facets: Array<{ label: string, value: string, count: number }>,
  tourGroupTypesList: Array<App.Tours.TourItemGroupType>,
): Array<Option> {
  return facets.map(({ label, value, count }) => {
    const enumKey = GROUP_TYPE_SLUG_TO_ENUM[value] ?? value
    const tourGroup = tourGroupTypesList.find((tourGroup) => tourGroup.id === enumKey)
    return {
      label: tourGroup?.name ?? label,
      value: enumKey,
      count,
    }
  })
}

function offerTagFacetsToOptions(
  facets: Array<{ label: string, value: string, count: number }>,
): Array<Option> {
  return facets.map(({ label, value, count }) => ({
    label,
    value: OFFER_TAG_SLUG_TO_ENUM[value] ?? value,
    count,
  }))
}

function convertTourGroupTypeFromFilterToArray(filters: App.OfferListFilters): Set<string> {
  const tourGroupTypeKeys = TOURS_GROUP_TYPES_LIST
  return tourGroupTypeKeys.reduce<Set<string>>((acc, key) => {
    if (filters[key]) acc.add(key)
    return acc
  }, new Set<string>())
}

function convertTourOfferTagsFromFilterToArray(filters: App.OfferListFilters): Set<string> {
  const tourOfferTagKeys = TOUR_OFFER_TAG_LIST
  return tourOfferTagKeys.reduce((acc, key) => {
    if (filters[key]) acc.add(key)
    return acc
  }, new Set<string>())
}

function convertTourProductTypesFromFilterToArray(filters: App.OfferListFilters): Set<string> {
  const { productTypes } = filters

  if (typeof productTypes === 'string') {
    return new Set(
      (productTypes as string)
        .split(',')
        .map(productType => productType.trim())
        .filter(Boolean),
    )
  }

  return new Set(productTypes)
}

interface RangeFilter {
  min: string | undefined
  max: string | undefined
}

interface ReducerState {
  localSort: App.OfferListSortByOptions | undefined
  localTourStyles: Set<string>
  localOperators: Set<string>
  localPriceRange: RangeFilter
  localTourLengthRange: RangeFilter
  localDestinations: Set<string>
  localGroupTypes: Set<string>
  localOfferTags: Set<string>
  localCategories: Set<string>
  localProductTypes: Set<string>
  localLuxPlusFeatures: Set<string>
  localAgentHubFeatures: Set<string>
  localCampaigns: Set<string>
}

type FilterKey = keyof ReducerState

function isSetFilter(value: any): value is Set<string> {
  return value instanceof Set
}

const convertToNumber = (value: string | number | undefined): number => {
  if (value === undefined) return 0
  if (typeof value === 'string') return parseInt(value)
  return value
}

function reducer(prev: ReducerState, next: Partial<ReducerState>) {
  return {
    ...prev,
    ...next,
  }
}

interface FilterChangeEvent {
  target: {
    value: string
  }
}

export type TourSearchPageFilterSubmit = {
  operators: Array<string>
  categories: Array<string>
  price: { start: number, end: number }
  duration: { start: number, end: number }
  numberOfPlacesVisited?: RangeFilter
  destinations: Array<string>
  groupTypes: Array<string>
  offerTags: Array<string>
  productTypes: Array<string>
  luxPlusFeatures: Array<string>
  agentHubFeatures: Array<string>
  campaigns: Array<string>
  sortBy?: string
}

interface Props {
  modalView?: boolean
  onApply?: (values: TourSearchPageFilterSubmit) => void
  searchFacetsState?: App.StatefulData<App.Tours.TourSearchFacets>
  initialFacets?: App.Tours.TourSearchFacets
  existingFilters?: App.OfferListFilters
  sortOptions?: Array<OfferListSortOption>
}

function convertFilterToFormFilterValue(filters: ReducerState): TourSearchPageFilterSubmit {
  return ({
    destinations: Array.from(filters.localDestinations ?? []),
    operators: Array.from(filters.localOperators ?? []),
    categories: Array.from(filters.localCategories ?? []),
    groupTypes: Array.from(filters.localGroupTypes ?? []),
    offerTags: Array.from(filters.localOfferTags ?? []),
    productTypes: Array.from(filters.localProductTypes ?? []),
    luxPlusFeatures: Array.from(filters.localLuxPlusFeatures ?? []),
    agentHubFeatures: Array.from(filters.localAgentHubFeatures ?? []),
    campaigns: Array.from(filters.localCampaigns ?? []),
    price: {
      start: parseInt(filters.localPriceRange.min ?? '0'),
      end: parseInt(filters.localPriceRange.max ?? '0'),
    },
    duration: {
      start: parseInt(filters.localTourLengthRange.min ?? '0'),
      end: parseInt(filters.localTourLengthRange.max ?? '0'),
    },
    sortBy: filters?.localSort,
  })
}

export interface TourSearchFilterContentElement {
  onFormReset: () => void
}

const defaultSortOptions: Array<OfferListSortOption> = []

const TourSearchFilterContent = forwardRef<TourSearchFilterContentElement, Props>((props, ref) => {
  const {
    modalView = false,
    existingFilters,
    onApply = noop,
    sortOptions = defaultSortOptions,
    searchFacetsState,
    initialFacets,
  } = props

  const intl = useIntl()
  const { getLabel: getCampaignFilterLabel } = useLoyaltyFilterLabels()
  const currentTakeover = useCurrentSiteTakeover({ vertical: 'tours' })
  const { SORT_OPTION_RECOMMENDED } = useOfferListSortOptions()
  const fetching = searchFacetsState?.fetching
  const searchFacets = searchFacetsState?.data
  const { currentRegionCode } = useContext(GeoContext)

  const luxPlusTourEnabled = useAppSelector(getIsLuxPlusToursEnabled)

  const locationsFacets = searchFacets?.locations?.length ? searchFacets.locations : initialFacets?.locations ?? EmptyArray
  const operatorFacets = searchFacets?.operators?.length ? searchFacets.operators : initialFacets?.operators ?? EmptyArray
  const groupTypeFacets = searchFacets?.groupTypes?.length ? searchFacets.groupTypes : initialFacets?.groupTypes ?? EmptyArray
  const categoryFacets = searchFacets?.categories?.length ? searchFacets.categories : initialFacets?.categories ?? EmptyArray
  const offerTagFacets = searchFacets?.offerTags?.length ? searchFacets.offerTags : initialFacets?.offerTags ?? EmptyArray
  const productTypeFacets = searchFacets?.offerTypes?.length ? searchFacets.offerTypes : initialFacets?.offerTypes ?? EmptyArray
  const luxPlusExclusiveFacets = searchFacets?.luxPlusExclusive?.length ? searchFacets.luxPlusExclusive : initialFacets?.luxPlusExclusive ?? EmptyArray
  const agentHubExclusiveFacets = searchFacets?.agentHubExclusive?.length ? searchFacets.agentHubExclusive : initialFacets?.agentHubExclusive ?? EmptyArray
  const campaignFacets = searchFacets?.campaigns?.length ? searchFacets.campaigns : initialFacets?.campaigns ?? EmptyArray
  const tourStyleFacets = searchFacets?.tourStyles?.length ? searchFacets.tourStyles : initialFacets?.tourStyles ?? EmptyArray

  const [destinationSearch, setDestinationSearch] = useState<string>('')

  const flexibleNights = useMemo<[string | undefined, string | undefined]>(() => {
    if (existingFilters?.flexibleNights) {
      return existingFilters.flexibleNights.split('-') as [string, string]
    }
    return [undefined, undefined]
  }, [existingFilters])

  const initialState: ReducerState = useMemo(
    () => {
      const minPrice = (initialFacets && !!initialFacets.price) ? initialFacets.price.min.toString() : '0'
      const maxPrice = (initialFacets && !!initialFacets.price) ? initialFacets.price.max.toString() : '0'
      return ({
        localTourStyles: new Set(),
        localOperators: new Set(),
        localPriceRange: {
          min: minPrice,
          max: maxPrice,
        },
        localTourLengthRange: {
          min: flexibleNights[0] ?? initialFacets?.tourLength?.min.toString(),
          max: flexibleNights[1] ?? initialFacets?.tourLength?.max.toString(),
        },
        localDestinations: new Set(),
        localGroupTypes: new Set(),
        localSort: SORT_OPTION_RECOMMENDED.value as App.OfferListSortByOptions,
        localCategories: new Set(),
        localOfferTags: new Set(),
        localProductTypes: new Set(),
        localLuxPlusFeatures: new Set(),
        localAgentHubFeatures: new Set(),
        localCampaigns: new Set(),
      })
    },
    [flexibleNights, initialFacets, SORT_OPTION_RECOMMENDED],
  )
  const [filters, updateFilters] = useReducer(reducer, initialState)
  const onFormReset = useCallback(() => {
    updateFilters(initialState)
    if (onApply) {
      onApply(convertFilterToFormFilterValue(initialState))
    }
  }, [initialState, onApply])

  useImperativeHandle(ref, () => ({ onFormReset }))

  const onFilterChange = useCallback(
    (key: FilterKey) => (e: FilterChangeEvent) => {
      const value = e.target.value
      const currentFilterValue = filters[key]
      const nextValue = isSetFilter(currentFilterValue) ?
          toggleSelectedSet(currentFilterValue, value) :
          new Set([value])
      const nextState = { ...filters, [key]: nextValue }
      updateFilters(nextState)
    },
    [filters, updateFilters],
  )

  const onFilterReset = useCallback(
    (key: FilterKey) => () => {
      const nextState = { ...filters, [key]: new Set() }
      updateFilters(nextState)
      if (onApply) {
        onApply(convertFilterToFormFilterValue(nextState))
      }
    },
    [filters, onApply],
  )

  useEffect(() => {
    updateFilters({
      localTourStyles: new Set(existingFilters?.tourStyles),
      localDestinations: new Set(existingFilters?.destinations),
      localOperators: new Set(existingFilters?.operators),
      localCategories: new Set(existingFilters?.categories),
      localGroupTypes: convertTourGroupTypeFromFilterToArray(existingFilters ?? {}),
      localOfferTags: convertTourOfferTagsFromFilterToArray(existingFilters ?? {}),
      localProductTypes: convertTourProductTypesFromFilterToArray(existingFilters ?? {}),
      localPriceRange: {
        min: existingFilters?.priceGte ?? initialFacets?.price?.min.toString(),
        max: existingFilters?.priceLte ?? initialFacets?.price?.max.toString(),
      },
      localTourLengthRange: {
        min: existingFilters?.tourLengthGte ?? flexibleNights[0] ?? searchFacets?.tourLength?.min.toString(),
        max: existingFilters?.tourLengthLte ?? flexibleNights[1] ?? searchFacets?.tourLength?.max.toString(),
      },
      localSort: existingFilters?.sortBy ?? SORT_OPTION_RECOMMENDED.value as App.OfferListSortByOptions,
      localLuxPlusFeatures: new Set(existingFilters?.luxPlusFeatures),
      localAgentHubFeatures: new Set(existingFilters?.agentHubFeatures),
      localCampaigns: new Set(existingFilters?.campaigns),
    })
  }, [
    existingFilters,
    searchFacets?.numberOfPlacesVisited?.max,
    searchFacets?.numberOfPlacesVisited?.min,
    searchFacets?.tourLength?.max,
    searchFacets?.tourLength?.min,
    initialFacets,
    flexibleNights,
    SORT_OPTION_RECOMMENDED,
  ])

  const [destinations, operators, groupTypes, categories, offerTags, productTypes, luxPlusFeatures, agentHubFeatures] = useMemo(
    () => [
      locationsFacets,
      operatorFacets,
      groupTypeFacetsToOptions(groupTypeFacets, getTourGroupTypesList(intl)),
      categoryFacets,
      offerTagFacetsToOptions(offerTagFacets),
      productTypeFacets,
      luxPlusExclusiveFacets.filter(feature => feature.count > 0),
      agentHubExclusiveFacets.filter(feature => feature.count > 0),
      campaignFacets,
    ],
    [locationsFacets, operatorFacets, groupTypeFacets, categoryFacets, offerTagFacets, productTypeFacets, luxPlusExclusiveFacets, agentHubExclusiveFacets, campaignFacets, intl],
  )

  const onTourStylesChange = onFilterChange('localTourStyles')
  const onOperatorsChange = onFilterChange('localOperators')
  const onOperatorsReset = onFilterReset('localOperators')

  const onCategoriesChange = onFilterChange('localCategories')

  const onDestinationsChange = useCallback((e: FilterChangeEvent) => {
    setDestinationSearch('')
    onFilterChange('localDestinations')(e)
  }, [onFilterChange])

  const onDestinationsReset = onFilterReset('localDestinations')

  const onGroupTypesChange = onFilterChange('localGroupTypes')
  const onGroupTypesReset = onFilterReset('localGroupTypes')

  const onOfferTagsChange = onFilterChange('localOfferTags')

  const onProductTypesChange = onFilterChange('localProductTypes')

  const onLuxPlusFeatureChange = onFilterChange('localLuxPlusFeatures')
  const onCampaignFeatureChange = onFilterChange('localCampaigns')

  const onAgentHubFeatureChange = onFilterChange('localAgentHubFeatures')

  const onCategoriesAndUltraLuxProductTypeReset = useCallback(() => {
    const nextState = {
      ...filters,
      localCategories: new Set<string>(),
      localProductTypes: new Set<string>(),
    }
    updateFilters(nextState)
    onApply(convertFilterToFormFilterValue(nextState))
  }, [filters, onApply])

  const onPriceRangeStartChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) =>
      updateFilters({
        localPriceRange: {
          ...filters.localPriceRange,
          min: e.target.value,
        },
      }),
    [filters.localPriceRange],
  )

  const onPriceRangeEndChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) =>
      updateFilters({
        localPriceRange: {
          ...filters.localPriceRange,
          max: e.target.value,
        },
      }),
    [filters.localPriceRange],
  )

  const priceSlider = useMemo(() => {
    if (initialFacets?.price) {
      const { min: pmin, max: pmax } = initialFacets.price
      const step = pmax <= 10000 ? 10 : 100
      const min = Math.floor(pmin / step) * step
      const max = Math.ceil(pmax / step) * step
      return { min, max, step }
    }
  }, [initialFacets?.price])

  const durationSlider = useMemo(() => {
    if (initialState?.localTourLengthRange.min && initialState?.localTourLengthRange.max) {
      const pmax = parseInt(initialState.localTourLengthRange.max)
      const pmin = parseInt(initialState.localTourLengthRange.min)
      const step = pmax <= 100 ? 1 : 10
      const min = Math.floor(pmin / step) * step
      const max = Math.ceil(pmax / step) * step
      return { min, max, step }
    }
  }, [initialState])

  const onTourLengthStartChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) =>
      updateFilters({
        localTourLengthRange: {
          ...filters.localTourLengthRange,
          min: e.target.value,
        },
      }),
    [filters.localTourLengthRange],
  )

  const onTourLengthEndChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) =>
      updateFilters({
        localTourLengthRange: {
          ...filters.localTourLengthRange,
          max: e.target.value,
        },
      }),
    [filters.localTourLengthRange],
  )

  const ultraLuxProductType = useMemo(
    () => productTypes
      .filter(productType => productType.value === 'ultra_lux_tour')
      .map(({ value, count }) => ({
        label: intl.formatMessage(productLabels.ultraLux),
        value,
        count,
      })),
    [intl, productTypes],
  )

  const destinationsItems = useMemo(
    () => generateOptionItems(destinations, filters.localDestinations, destinationSearch, intl),
    [destinations, filters.localDestinations, destinationSearch, intl],
  )

  const operatorsItems = useMemo(
    () => generateOptionItems(operators, filters.localOperators, '', intl),
    [filters.localOperators, operators, intl],
  )

  const categoriesItems = useMemo(
    () => generateOptionItems(categories, filters.localCategories, '', intl),
    [filters.localCategories, categories, intl],
  )

  const groupTypesItems = useMemo(
    () => generateOptionItems(groupTypes, filters.localGroupTypes, '', intl),
    [filters.localGroupTypes, groupTypes, intl],
  )

  const offerTagItems = useMemo(
    () => generateOptionItems(offerTags, filters.localOfferTags, '', intl),
    [filters.localOfferTags, offerTags, intl],
  )

  const tourStyles = useMemo(
    () => generateOptionItems(tourStyleFacets, filters.localTourStyles, '', intl),
    [tourStyleFacets, filters.localTourStyles, intl],
  )

  const ultraLuxItem = useMemo(
    () => generateOptionItems(ultraLuxProductType, filters.localProductTypes, '', intl),
    [filters.localProductTypes, ultraLuxProductType, intl],
  )

  const luxPlusFeatureItems = useMemo(
    () => generateOptionItems(luxPlusFeatures, filters.localLuxPlusFeatures, '', intl),
    [filters.localLuxPlusFeatures, luxPlusFeatures, intl],
  )

  const agentHubFeatureItems = useMemo(
    () => generateOptionItems(agentHubFeatures, filters.localAgentHubFeatures, '', intl),
    [filters.localAgentHubFeatures, agentHubFeatures, intl],
  )

  const specialOfferItems = useMemo(() => {
    if (!currentRegionCode) {
      return []
    }

    const specialOfferFacets = TOUR_SEARCH_SPECIAL_OFFERS_CAMPAIGNS.flatMap((campaign) => {
      if (!isTourSearchSpecialOfferCampaignActive(campaign, currentRegionCode, config.BRAND)) {
        return []
      }

      const matchingFacet = campaignFacets.find(facet => facet.value === campaign.tag)

      if (!matchingFacet) {
        return []
      }

      return [{
        ...matchingFacet,
        label: intl.formatMessage(specialOfferMessages[campaign.intlKey]),
      }]
    })

    return generateOptionItems(specialOfferFacets, filters.localCampaigns, '', intl)
  }, [currentRegionCode, intl, campaignFacets, filters.localCampaigns])

  const societeCampaignItems = useMemo(() => {
    const isTakeoverActive = currentTakeover?.id === triplePointsTakeoverId

    if (!isTakeoverActive) {
      return []
    }

    const triplePointsFacet = campaignFacets.find(facet => facet.value === triplePointCampaignId)

    return generateOptionItems([{
      value: triplePointCampaignId,
      label: getCampaignFilterLabel(triplePointCampaignId),
      count: triplePointsFacet?.count ?? 0,
    }], filters.localCampaigns, '', intl)
  }, [campaignFacets, currentTakeover?.id, filters.localCampaigns, getCampaignFilterLabel, intl])

  const hasUltraLux = useMemo(
    () => ultraLuxProductType.length !== 0,
    [ultraLuxProductType],
  )

  const hasCategories = useMemo(
    () => categories.length > 0,
    [categories],
  )

  const hasOfferTagFacet = useMemo(
    () => ((offerTags ?? [])
      .some(offerTag => (offerTag.count ?? 0) > 0)),
    [offerTags],
  )

  const hasCategoriesOrUltraLux = useMemo(
    () => hasUltraLux || hasCategories,
    [hasCategories, hasUltraLux],
  )

  const hasLuxPlusFeatures = useMemo(
    () => luxPlusFeatures.length > 0,
    [luxPlusFeatures],
  )

  const hasAgentHubFeatures = useMemo(
    () => agentHubFeatures.length > 0,
    [agentHubFeatures],
  )

  const noFilterAvailable =
    !fetching &&
    Math.max(destinations.length, operators.length, groupTypes.length, categories.length, offerTags.length, ultraLuxProductType.length, specialOfferItems.length, societeCampaignItems.length) == 0 &&
    searchFacets?.price === undefined &&
    searchFacets?.tourLength === undefined &&
    searchFacets?.numberOfPlacesVisited === undefined

  const sortAndFilterLabel = intl.formatMessage(
    {
      id: 'p_uVvhO_XXuvKDtPxDVh',
      defaultMessage: 'Sort & Filter',
      description: 'Heading when both sort and filter options are available',
    },
  )
  const filterByLabel = intl.formatMessage(
    {
      id: 'KuUwOVozrpLnpLROpPpv',
      defaultMessage: 'Filter By',
      description: 'Heading when only filter options are available (no sort)',
    },
  )
  const sortAndFiltersText =
    sortOptions && sortOptions.length > 0 ? sortAndFilterLabel : filterByLabel

  const nbFacetOptionSelected = useMemo(() => {
    return (filters.localOperators?.size ?? 0) +
      (filters.localDestinations?.size ?? 0) +
      (filters.localGroupTypes?.size ?? 0) +
      (filters.localCategories?.size ?? 0) +
      (filters.localOfferTags?.size ?? 0) +
      (filters.localProductTypes?.size ?? 0) +
      (filters.localLuxPlusFeatures?.size ?? 0) +
      (filters.localCampaigns?.size ?? 0)
  }, [filters.localGroupTypes, filters.localOperators, filters.localDestinations, filters.localCategories, filters.localOfferTags, filters.localProductTypes, filters.localLuxPlusFeatures, filters.localCampaigns])

  const durationMin = useMemo(() => max([filters.localTourLengthRange.min, durationSlider?.min, 0], convertToNumber) as number, [filters.localTourLengthRange.min, durationSlider?.min])
  return (
    <>
      {!modalView && <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <Heading variant="heading4">{sortAndFiltersText}</Heading>
        {nbFacetOptionSelected > 0 && <ActionTextButton kind="tertiary" onClick={onFormReset}>{intl.formatMessage(
          {
            id: 'KrseDX85F2cS_0nsBfZG',
            defaultMessage: 'Reset all ({count})',
            description: 'Button to reset all selected filters, showing the number of active filters',
          },
          { count: nbFacetOptionSelected },
        )}
        </ActionTextButton>}
      </Group>}

      {sortOptions && sortOptions.length > 0 && (
        <CSSBreakpoint max="mobile">
          <FilterPanelFilterGroup title={intl.formatMessage(
            {
              id: 'pU0CK2vzbR07BxQAUBT4',
              defaultMessage: 'Sort by',
              description: 'Title for the sort options filter group in the search or listing filters panel',
            },
          )}
          >
            <InputGroup direction="vertical" gap={12}>
              {sortOptions.map((sortOption) => (
                <RadioInput
                  key={sortOption.value}
                  name="sortBy"
                  value={sortOption.value}
                  onChange={() =>
                    updateFilters({ localSort: sortOption.value as App.OfferListSortByOptions })}
                  checked={sortOption.value === filters.localSort}
                >
                  {sortOption.label}
                </RadioInput>
              ))}
            </InputGroup>
          </FilterPanelFilterGroup>
        </CSSBreakpoint>
      )}
      {fetching && (
        <ColourfulLoadingAnimation size={8} />
      )}
      {noFilterAvailable && (
        <BodyText variant="large">
          {intl.formatMessage(
            {
              id: 'SLl8H2qDvzOjPY1CBCPY',
              defaultMessage: 'No filters are available for your search.',
              description: 'Empty state message when no filter options are available for the current search',
            },
          )}
        </BodyText>
      )}
      { luxPlusTourEnabled && hasLuxPlusFeatures && <FilterPanelFilterGroup loading={fetching} title={<LuxPlusLabel type="only-logo" />}>
        <FilterPanelCheckboxGroup
          name="luxPlusFeatures"
          items={luxPlusFeatureItems}
          onChange={onLuxPlusFeatureChange}
          maxItems={5}
        />
      </FilterPanelFilterGroup>}
      <TourSearchSocieteFilters>
        {societeCampaignItems.length > 0 && (
          <FilterPanelCheckboxGroup
            name="campaigns"
            items={societeCampaignItems}
            onChange={onCampaignFeatureChange}
          />
        )}
      </TourSearchSocieteFilters>
      {specialOfferItems.length > 0 && <FilterPanelFilterGroup
        loading={fetching}
        title={intl.formatMessage(
          {
            id: 'EukyLYI9FnmwlXJHwThN',
            defaultMessage: 'Special Offers',
            description: 'Title for the special offers filter group on the tours search page',
          },
        )}
      >
        <FilterPanelCheckboxGroup
          name="campaigns"
          items={specialOfferItems}
          onChange={onCampaignFeatureChange}
          maxItems={5}
        />
      </FilterPanelFilterGroup>}
      {isAgentHubEnabled && hasAgentHubFeatures && <FilterPanelFilterGroup
        loading={fetching}
        title={intl.formatMessage(
          {
            id: '1rlEkPcOJDnQH7EHzC-N',
            defaultMessage: '{brandName} Features',
            description: 'Heading title for the agent hub features filter group',
          },
          { brandName: agentHubOrSubBrandConfig.displayShortName },
        )}
      >
        <FilterPanelCheckboxGroup
          name="agentHubFeatures"
          items={agentHubFeatureItems}
          onChange={onAgentHubFeatureChange}
          maxItems={5}
        />
      </FilterPanelFilterGroup>}
      {(priceSlider || hasOfferTagFacet) && !fetching && (
        <FilterPanelFilterGroup title={intl.formatMessage(
          {
            id: 'b_tSUufqm1OtOz8im2xA',
            defaultMessage: 'Price',
            description: 'Title for the price range filter group in the search or listing filters panel',
          },
        )}
        >
          {priceSlider && (
            <DualSliderInput
              name="price"
              format="currency"
              min={priceSlider.min}
              max={priceSlider.max}
              startValue={Number(filters.localPriceRange.min)}
              endValue={Number(filters.localPriceRange.max)}
              step={priceSlider.step}
              onStartChange={onPriceRangeStartChange}
              onEndChange={onPriceRangeEndChange}
              variant="label"
            />
          )}
          {hasOfferTagFacet && (
            <FilterPanelCheckboxGroup
              name="offerTags"
              items={offerTagItems}
              onChange={onOfferTagsChange}
              maxItems={10}
            />
          )}
        </FilterPanelFilterGroup>
      )}
      {!!durationSlider && !!filters.localTourLengthRange.max && !!filters.localTourLengthRange.min && !fetching && (
        <FilterPanelFilterGroup title={intl.formatMessage(
          {
            id: '7wA5l94LX7RTazFwToYJ',
            defaultMessage: 'Duration',
            description: 'Title for the tour duration filter group on the tour page',
          },
        )}
        >
          <BodyText variant="medium">
            {intl.formatMessage(
              {
                id: 'DtPoRtqoFipNxCfj1KhD',
                defaultMessage: '{minText} - {maxText}',
                description: 'Duration range for tour search filter slider, e.g. "3 days - 10 days"',
              },
              {
                minText: intl.formatMessage(
                  {
                    id: 'UtSxW7HC8fmPeg8N9JXy',
                    defaultMessage: '{count, plural, one {# day} other {# days}}',
                    description: 'Minimum duration in days for tour search filter slider',
                  },
                  { count: Number(durationMin) },
                ),
                maxText: intl.formatMessage(
                  {
                    id: 'VQgKEgKaUCStvWoXARYW',
                    defaultMessage: '{count, plural, one {# day} other {# days}}',
                    description: 'Maximum duration in days for tour search filter slider',
                  },
                  { count: Number(filters.localTourLengthRange.max) },
                ),
              },
            )}
          </BodyText>
          <DualSlider
            name="duration"
            min={parseInt(initialState.localTourLengthRange.min ?? '0')}
            max={parseInt(initialState.localTourLengthRange.max ?? '0')}
            defaultStartValue={durationMin}
            defaultEndValue={Number(filters.localTourLengthRange.max)}
            startValue={Number(filters.localTourLengthRange.min)}
            endValue={Number(filters.localTourLengthRange.max)}
            step={durationSlider.step}
            onStartChange={onTourLengthStartChange}
            onEndChange={onTourLengthEndChange}
          />
        </FilterPanelFilterGroup>
      )}
      {(!fetching && !!tourStyles.length) && <FilterPanelFilterGroup title={intl.formatMessage(
        {
          id: 'WrpasdGDyxbdHFTu7f1w',
          defaultMessage: 'Tour Style',
          description: 'Title for the tour style filter group on the tour offer detail page',
        },
      )}
      >
        <FilterPanelCheckboxGroup
          name="tourStyles"
          items={tourStyles}
          onChange={onTourStylesChange}
        />
      </FilterPanelFilterGroup>}
      {hasCategoriesOrUltraLux && !fetching && (
        <FilterPanelFilterGroup
          title={intl.formatMessage(
            {
              id: 'rzsdhwXcHoBp8W5FW_2O',
              defaultMessage: 'Category',
              description: 'Title for the tour category filter group on the tour page',
            },
          )}
          canReset={filters.localCategories.size > 0 || filters.localProductTypes.size > 0}
          onReset={onCategoriesAndUltraLuxProductTypeReset}
        >
          {hasUltraLux && (
            <FilterPanelCheckboxGroup
              name="productTypes"
              items={ultraLuxItem}
              onChange={onProductTypesChange}
              maxItems={1}
            />
          )}
          {hasCategories && (
            <FilterPanelCheckboxGroup
              name="categories"
              items={categoriesItems}
              onChange={onCategoriesChange}
              maxItems={10}
            />
          )}
        </FilterPanelFilterGroup>
      )}
      {operators.length > 0 && !fetching && (
        <FilterPanelFilterGroup
          title={intl.formatMessage(
            {
              id: 'pEVByXVOoFsC3bRfRerb',
              defaultMessage: 'Operator',
              description: 'Title for the tour operator filter group on the tour page',
            },
          )}
          canReset={filters.localOperators.size > 0}
          onReset={onOperatorsReset}
        >
          <FilterPanelCheckboxGroup
            name="operators"
            items={operatorsItems}
            onChange={onOperatorsChange}
            maxItems={10}
          />
        </FilterPanelFilterGroup>
      )}
      {destinations.length > 0 && !fetching && (
        <FilterPanelFilterGroup
          title={intl.formatMessage(
            {
              id: 'pAcjxtn4jZlE050MF6_5',
              defaultMessage: 'Destinations',
              description: 'Title for the destinations filter group in the search or listing filters panel',
            },
          )}
          canReset={filters.localDestinations.size > 0}
          onReset={onDestinationsReset}
        >
          <FilterSearchInput
            placeholder={intl.formatMessage(
              {
                id: 'ZAydku_JlV7yPKg2tr7i',
                defaultMessage: 'Search for stops',
                description: 'Placeholder text for the destination search input in tour filters',
              },
            )}
            onChange={setDestinationSearch}
            name="destinationSearch"
            value={destinationSearch}
          />
          <FilterPanelCheckboxGroup
            name="destinations"
            items={destinationsItems}
            onChange={onDestinationsChange}
            maxItems={5}
          />
        </FilterPanelFilterGroup>
      )}
      {groupTypes.length > 0 && !fetching && (
        <FilterPanelFilterGroup
          title={intl.formatMessage(
            {
              id: 'pso0h-c8ulJDqsQ6QJ-G',
              defaultMessage: 'Group type',
              description: 'Title for the group type filter group (e.g. private, small group)',
            },
          )}
          canReset={filters.localGroupTypes.size > 0}
          onReset={onGroupTypesReset}
        >
          <FilterPanelCheckboxGroup
            name="groupTypes"
            items={groupTypesItems}
            onChange={onGroupTypesChange}
            maxItems={10}
          />
        </FilterPanelFilterGroup>
      )}
    </>
  )
})

export default TourSearchFilterContent
